import { inject, Service } from '@angular/core';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { DeleteBuildType } from '@portal-core/builds/enums/delete-build-type.enum';
import { ProcessBuildType } from '@portal-core/builds/enums/process-build-type.enum';
import { BuildLanguageBranch } from '@portal-core/builds/models/build-language-branch.model';
import { BuildSchedule, BuildScheduleSaveModel } from '@portal-core/builds/models/build-schedule.model';
import { Build } from '@portal-core/builds/models/build.model';
import { DeleteBuildsResult } from '@portal-core/builds/models/delete-builds-result.model';
import { BuildsApiService } from '@portal-core/builds/services/builds-api.service';
import { BuildsDataService } from '@portal-core/builds/services/builds-data.service';
import { BuildsScheduleDataService } from '@portal-core/builds/services/builds-schedule-data.service';
import { BuildOperationType } from '@portal-core/builds/types/build-operation-type.type';
import { AccessMap, AccessRequest } from '@portal-core/data/types/access-request.type';
import { CollectionServiceBase } from '@portal-core/data/collection/services/collection.service.base';
import { StringService } from '@portal-core/general/services/string.service';
import { ProcessAttemptStatus } from '@portal-core/processes/enums/process-attempt-status.enum';
import { Resettable } from '@portal-core/util/resettable.decorator';
import { map, Observable, tap } from 'rxjs';

@Service()
@Resettable()
export class BuildsService extends CollectionServiceBase<Build> {
  private readonly buildApiService = inject(BuildsApiService);
  private readonly buildsScheduleDataService = inject(BuildsScheduleDataService);
  private readonly stringService = inject(StringService);
  readonly viewableOutputExtensions: string[] = ['htm', 'html', 'pdf'];


  constructor() {
    super(inject(BuildsDataService));
  }

  protected override fetchItemById$(itemId: number): Observable<Build> {
    return this.buildApiService.getBuild$(itemId).pipe(
      map(build => this.processBuild(build))
    );
  }

  protected override fetchItemsById$(itemIds: number[]): Observable<Build[]> {
    return this.buildApiService.getBuildsByIds$(itemIds).pipe(
      map(builds => Array.isArray(builds) ? this.processBuilds(builds) : null)
    );
  }

  getAccess$(request: AccessRequest<Build, BuildOperationType>): Observable<AccessMap<BuildOperationType>> {
    return this.buildApiService.getAccess$(request);
  }

  getBuildsPageByProjectId$(projectId: number, filter: PageFilter): Observable<Page<Build>> {
    return this.buildApiService.getBuildsPageByProjectId$(projectId, filter).pipe(
      tap(page => this.processBuildsPage(page))
    );
  }

  getBuildsPage$(filter: PageFilter): Observable<Page<Build>> {
    return this.buildApiService.getBuildsPage$(filter).pipe(
      tap(page => this.processBuildsPage(page))
    );
  }

  bulkDeleteBuilds$(projectId: number, buildIds: number[]): Observable<MadCloudResult<void>> {
    return this.buildApiService.bulkDeleteBuilds$(projectId, buildIds).pipe(
      tap(() => this.deleteItems$(buildIds))
    );
  }

  deleteBuildsInProject$(projectId: number, deleteBuildType: DeleteBuildType): Observable<MadCloudResult<DeleteBuildsResult>> {
    return this.buildApiService.deleteBuildsInProject$(projectId, deleteBuildType);
  }

  bulkDeleteBuildScheduleById$(projectId: number, buildScheduleIds: number[]): Observable<void> {
    return this.buildApiService.bulkDeleteBuildSchedulesById$(projectId, buildScheduleIds).pipe(
      tap(() => {
        this.buildsScheduleDataService.deleteItems$(buildScheduleIds);
      })
    );
  }

  createBuild$(projectId: number, currentTargetPath: string, currentBranchName: string, description: string, buildLanguageBranches: BuildLanguageBranch[], destinationFilePaths: string[]): Observable<Build> {
    return this.buildApiService.createBuild$(projectId, currentTargetPath, currentBranchName, description, buildLanguageBranches, destinationFilePaths);
  }

  processBuild$(buildId: number, processBuildType: ProcessBuildType): Observable<void> {
    return this.buildApiService.processBuild$(buildId, processBuildType);
  }

  getDownloadBuildSasToken$(buildId: number): Observable<string> {
    return this.buildApiService.getDownloadBuildSasToken$(buildId);
  }

  setBuildAsKeep$(buildId: number, keep: boolean): Observable<MadCloudResult<void>> {
    return this.buildApiService.setBuildAsKeep$(buildId, keep);
  }

  setUserViewedBuild$(buildId: number): Observable<void> {
    return this.buildApiService.setUserViewedBuild$(buildId);
  }

  updateBuildSettings$(build: Build, keepBuild: boolean, description: string): Observable<MadCloudResult<void>> {
    return this.buildApiService.updateBuildSettings$(build.Id, keepBuild, description);
  }

  upsertBuildSchedule$(projectId: number, buildSchedule: BuildScheduleSaveModel): Observable<BuildSchedule> {
    return this.buildApiService.upsertBuildSchedule$(projectId, buildSchedule).pipe(
      tap(savedBuildSchedule => this.buildsScheduleDataService.updateItems$([savedBuildSchedule]))
    );
  }

  updateBuild$(build: Build): Observable<void> {
    this.addItemsToListById$('Targets', build.Id, [build]);
    return this.updateItems$([this.processBuild(build)]);
  }

  processBuildsPage(page: Page<Build>): Page<Build> {
    if (Array.isArray(page?.Items)) {
      this.processBuilds(page.Items);
    }
    return page;
  }

  processBuilds(builds: Build[]): Build[] {
    builds.forEach(build => this.processBuild(build));
    return builds;
  }

  processBuild(build: Build): Build {
    if (build) {
      const status = build.Process?.LastProcessAttempt?.Status;
      const isSuccess = status === ProcessAttemptStatus.Complete;
      // Treat a pre-pickup-canceled build (CanceledByUserId set, no attempt yet) as finished
      // for the same reasons the terminal statuses are: nothing else is going to happen to it.
      const isFinished = BuildsService.isTerminalStatus(status) || this.isCanceled(build);

      build.CanBeKeep = isSuccess;
      build.CanViewOutput = false;
      build.CanBeEdited = isSuccess;
      build.CanBeCanceled = !isFinished;
      build.CanBeDeleted = isFinished &&
        !build.IsKept &&
        (!build.SitesList || build.SitesList.length === 0); // cannot delete builds that are kept or assigned to site
      build.CanDownload = isSuccess;
      build.CanBeAssignedToSite = isSuccess;
      build.CanDownloadLog = isFinished;
      build.LinkedToLiveSite = Array.isArray(build.SitesList) && build.SitesList.some(s => s.IsLive);
      build.IsFinished = isFinished;
      build.IsStopped = isFinished;
      if (isSuccess && build.PublishStartupFile) {
        const fileExt = build.PublishStartupFile.split('.').pop();
        build.CanViewOutput = this.viewableOutputExtensions.includes(fileExt.toLowerCase());
      }

      if (isFinished) {
        build.Completed = build.Process?.LastProcessAttempt?.EndedOn;
      }

      // ProcessAttempt.Progress is [0, 1] (1 forced if Completed); mat-progress-bar
      // wants [0, 100]. Null means no progress event has arrived yet — render as 0.
      const progress = isFinished ? 1 : build.Process?.LastProcessAttempt?.Progress ?? 0;
      build.Progress = progress * 100;
    }

    return build;
  }

  /**
   * Build/publication is in a terminal state — no more work will happen on this attempt.
   * Treat Aborted (worker died) the same as Failed/Canceled for UX purposes: stopped, no
   * further updates incoming.
   */
  private static isTerminalStatus(status: ProcessAttemptStatus | null): boolean {
    return status === ProcessAttemptStatus.Complete
      || status === ProcessAttemptStatus.Failed
      || status === ProcessAttemptStatus.Canceled
      || status === ProcessAttemptStatus.Aborted;
  }

  /**
   * True when the build is queued and waiting for the worker. Includes the pre-pickup
   * window where the worker hasn't yet created a ProcessAttempt — the build has been
   * enqueued but no attempt row exists yet, which is still "queued" from the user's
   * perspective. A pre-pickup canceled build is excluded so it doesn't show as both.
   */
  isQueued(build: Build | null | undefined): boolean {
    const attempt = build?.Process?.LastProcessAttempt;
    if (!attempt) {
      return !!build && !build.CanceledByUserId;
    }
    return attempt.Status === ProcessAttemptStatus.Queued;
  }

  /**
   * True when the build was canceled. Includes the pre-pickup window where the user
   * cancelled a queued build before the worker created an attempt — Build.CanceledByUserId
   * is set but no ProcessAttempt with Status=Canceled exists yet.
   */
  isCanceled(build: Build | null | undefined): boolean {
    if (build?.Process?.LastProcessAttempt?.Status === ProcessAttemptStatus.Canceled) {
      return true;
    }
    return !!build?.CanceledByUserId && !build?.Process?.LastProcessAttempt;
  }

  /**
   * Like {@link getColorForState} but handles the pre-pickup queued/canceled cases (when
   * the build has no ProcessAttempt yet). Prefer this over reaching into
   * `build.Process.LastProcessAttempt.Status` at call sites.
   */
  getColorForBuild(build: Build | null | undefined, paletteShade: '' | 'muted' | 'vivid' = ''): string {
    if (this.isCanceled(build)) {
      return this.getColorForState(ProcessAttemptStatus.Canceled, paletteShade);
    }
    if (this.isQueued(build)) {
      return this.getColorForState(ProcessAttemptStatus.Queued, paletteShade);
    }
    return this.getColorForState(build?.Process?.LastProcessAttempt?.Status ?? null, paletteShade);
  }

  getColorForState(status: ProcessAttemptStatus | null, paletteShade: '' | 'muted' | 'vivid' = ''): string {
    let paletteName: string;
    switch (status) {
      case ProcessAttemptStatus.Complete:
        paletteName = 'build-success';
        break;
      case ProcessAttemptStatus.Failed:
      case ProcessAttemptStatus.Aborted:
        paletteName = 'build-fail';
        break;
      case ProcessAttemptStatus.Canceled:
        paletteName = 'build-cancel';
        break;
      case ProcessAttemptStatus.Queued:
        paletteName = 'info';
        break;
      case ProcessAttemptStatus.Running:
        paletteName = 'build-success';
        break;
      default:
        paletteName = 'build-unknown';
        break;
    }

    return `${paletteName}${paletteShade ? '-' + paletteShade : ''}`;
  }

  /**
   * User-facing label for a build's current state. Terminal/Queued statuses short-circuit
   * on Status alone; for Running, reduces the raw LastEvent (camelCased BuildEventType
   * name) to a coarse phase string via {@link BuildsService.buildPhaseMap}. Unmapped events
   * fall through to a sentence-case formatter so a future event still renders sensibly.
   */
  getStatusDisplayText(build: Build | null | undefined): string {
    // Handle pre-pickup cases first — isCanceled/isQueued cover both the explicit-status form
    // and the no-attempt-yet form.
    if (this.isCanceled(build)) {
      return 'Canceled';
    }
    if (this.isQueued(build)) {
      return 'Queued';
    }

    const attempt = build?.Process?.LastProcessAttempt;
    switch (attempt?.Status) {
      case ProcessAttemptStatus.Complete:
        return 'Complete';
      case ProcessAttemptStatus.Failed:
        return 'Failed';
      case ProcessAttemptStatus.Aborted:
        return 'Aborted';
      case ProcessAttemptStatus.Running:
        if (!attempt?.LastEvent) {
          return 'Running';
        }
        return BuildsService.buildPhaseMap[attempt.LastEvent] ?? this.stringService.sentenceCase(attempt.LastEvent);
      default:
        return '';
    }
  }

  private static readonly buildPhaseMap: Readonly<Record<string, string>> = {
    // Getting Files
    gettingSource: 'Getting Files',
    gettingSourceComplete: 'Getting Files',
    buildProcessing: 'Getting Files',
    // Building
    buildStart: 'Building',
    compileStart: 'Building',
    compileComplete: 'Building',
    targetStart: 'Building',
    targetComplete: 'Building',
    batchBuildStart: 'Building',
    batchBuildComplete: 'Building',
    updateBuildInfo: 'Building',
    updateBuildInfoComplete: 'Building',
    synchronizeTargets: 'Building',
    synchronizeTargetsComplete: 'Building',
    // Publishing
    compressOutput: 'Publishing',
    compressOutputComplete: 'Publishing',
    publishStart: 'Publishing',
    publishComplete: 'Publishing'
  };

  // TODO: circular dependency if adding ProjectTargetService - need to fix that and not use getZipNameFromTargetPath.
  getZipNameForBuild(build: Build): string {
    return this.getZipNameFromTargetPath(build.TargetPath) + '_' + build.Id + '.zip';
  }

  getZipNameFromTargetPath(targetPath: string): string {
    if (targetPath) {
      const splitTargetPath = targetPath.split('/');
      return splitTargetPath[splitTargetPath.length - 1]?.split('.fltar')[0];
    }
  }

  getBuildByTargetPath$(projectId: number, branchName: string, targetPath: string): Observable<Build> {
    return this.buildApiService.getBuildByTargetPath$(projectId, branchName, targetPath);
  }

  getBuildByProjectIdAndPath$(projectId: number, targetPath: string): Observable<Build> {
    return this.buildApiService.getBuildByProjectIdAndPath$(projectId, targetPath);
  }

  getBuildLogStreamUrl(buildId: number): string {
    return this.buildApiService.getBuildLogStreamUrl(buildId);
  }
}
